import {memo, useCallback, useEffect, useRef, useState} from 'react';
import styles from './index.module.scss';
import cx from "classnames";
import {Swiper, SwiperProps, SwiperSlide} from "swiper/react";
import "swiper/css";
import arr from '../../../../assets/icons/arrows/arrow_down.svg';

import {debounce} from "../../../../helpers/utils";

interface Props {
  items: {
    url: string,
    alt: string
  }[],
  showPreview: boolean
}

const VISIBLE_THUMBNAILS_IMAGES = 4;

const PhotoGallery = ({items, showPreview}: Props) => {
  const ignoreScrollEvents = useRef(false);
  const smallSlider = useRef<HTMLDivElement | null>(null);
  const bigSlider = useRef<any | null>(null);
  const [activeImgIndex, setActiveImgIndex] = useState(0);
  const length = items.length;

  const selectTab = useCallback((imgIndex: number | SwiperProps) => {
    const index = typeof imgIndex === 'number' ? imgIndex : imgIndex.activeIndex
    if (!bigSlider.current || activeImgIndex === index) return false
    // setBlurImage(true)
    bigSlider.current?.slideTo(index)
    setActiveImgIndex(index)
  }, [activeImgIndex]);

  useEffect(() => {
    let isTouching = false;
    let scrollTimeout: any;
    let scrollEvent: any;
    const calculateClosestScrollPoint = (leftScroll: number, thumbnailSize: number) => {
      const currentPosition = leftScroll % thumbnailSize;
      return currentPosition < (thumbnailSize - currentPosition) ? -currentPosition : thumbnailSize - currentPosition
    }

    const onScrollEnd = (e: any = scrollEvent) => {
      scrollEvent = null;
      if (ignoreScrollEvents.current) return ignoreScrollEvents.current = false;

      const {scrollLeft, clientWidth, offsetWidth, scrollWidth} = e.target;
      const thumbnailWidth = clientWidth / VISIBLE_THUMBNAILS_IMAGES;
      const scrollTo = calculateClosestScrollPoint(scrollLeft, thumbnailWidth);
      const hasReachedEnd = scrollLeft + offsetWidth < scrollWidth;

      selectTab(Math.round((scrollLeft + scrollTo) / thumbnailWidth))
      if (hasReachedEnd && Math.abs(scrollLeft) > 1) {
        ignoreScrollEvents.current = true
        smallSlider.current?.scrollTo({
          left: scrollLeft + scrollTo,
          behavior: 'smooth'
        })
      }
    }
    const debounceEvent = debounce((e: any) => {
      if (isTouching) {
        // If the user is currently touching, reset the timeout
        clearTimeout(scrollTimeout);
      } else {
        scrollEvent = e;
        // If the user is not touching, start or reset the timeout
        onScrollEnd(e)
      }
    }, 200);

    const onTouchStart = () => {
      isTouching = true;
    }

    const onTouchEnd = () => {
      isTouching = false;
      // Start a timeout when the user stops touching
      debounce(onScrollEnd, 200);
    }

    //@ts-ignore
    if (smallSlider.current?.onscrollend !== undefined) smallSlider.current?.addEventListener('scrollend', onScrollEnd)
    else if (smallSlider.current?.ontouchend !== undefined) {
      smallSlider.current?.addEventListener('scroll', debounceEvent)
      smallSlider.current?.addEventListener('touchstart', onTouchStart)
      smallSlider.current?.addEventListener('touchend', onTouchEnd)
    }

    return () => {
      smallSlider.current?.removeEventListener('scrollend', onScrollEnd)
      smallSlider.current?.removeEventListener('scroll', debounceEvent)
      smallSlider.current?.removeEventListener('touchstart', onTouchStart)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      smallSlider.current?.removeEventListener('touchend', onTouchEnd)
    }
  }, [selectTab]);

  useEffect(() => {
    const preventScroll = items.length - activeImgIndex < VISIBLE_THUMBNAILS_IMAGES;
    if (!smallSlider.current) return
    const {clientWidth, scrollLeft, scrollWidth} = smallSlider.current;
    if (preventScroll) {
      ignoreScrollEvents.current = true
      return smallSlider.current?.scrollTo({
        left: scrollWidth - clientWidth,
        behavior: 'smooth'
      })
    }
    const thumbnailWidth = clientWidth / VISIBLE_THUMBNAILS_IMAGES;
    const scrollTo = thumbnailWidth * activeImgIndex;
    if (scrollLeft === scrollTo) return

    smallSlider.current?.scrollTo({
      left: scrollTo,
      behavior: 'smooth'
    })
  }, [activeImgIndex, items])

  return (
    <div className={styles.root}>
      <div className={styles.mainImg}>
        <div className={styles.arrow} onClick={selectTab.bind(null, activeImgIndex - 1 < 0 ? 0 : activeImgIndex - 1)}>
          <img src={arr} alt="Left"/>
        </div>
        <Swiper onSwiper={(swiper: any) => (bigSlider.current = swiper)} onSlideChange={selectTab} spaceBetween={8}>
          {items.map(({url, alt}, i) => (
            <SwiperSlide key={url + i}>
              <img className={styles.img} src={url} alt={alt} loading={i !== 0 ? 'lazy' : undefined}/>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className={styles.arrow} onClick={selectTab.bind(null, Math.min(activeImgIndex + 1, length - 1))}>
          <img src={arr} alt="Right"/>
        </div>
        <p className={styles.count}>{activeImgIndex + 1}/{length}</p>
      </div>

      <div className={styles.dots}>
        {items.map((_, i) => (
          <div key={i} className={cx(styles.dots__item, {[styles.active]: activeImgIndex === i})}
               onClick={selectTab.bind(null, i)}>
            <div/>
          </div>
        ))}
      </div>

      {showPreview && (
        <div className={styles.slider}>
          <div className={styles.overlay} ref={smallSlider}>
            {items.map(( {url, alt}, i ) => {
              return (
                <div className={styles.smallImg} key={i} onClick={selectTab.bind(null, i)}>
                  <img src={url} alt={alt} loading={i > 3 ? 'lazy' : undefined}/>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default memo(PhotoGallery)
